import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import SelectButton from '../components/ui/SelectButton';
import ModernFileUploader from '../components/ui/ModernFileUploader';
import { getUserProfile, getFamily, updateFamily, updateUserProfile } from '../services/userService';
import { getActiveRegions, Region } from '../services/regionService';
import { getActiveTestPackages, TestPackage } from '../services/testPackageService';
import { getActiveInsurances, Insurance } from '../services/insuranceService';
import { getActiveSupplementaryInsurances, SupplementaryInsurance } from '../services/supplementaryInsuranceService';
import { getMessages, MessageCenterData } from '../services/messageCenterService';
import { toast } from 'react-toastify';
import API_CONFIG from '../config/api';
import { validateMobileNumber, validateIranianNationalCode, cleanDigitsOnly } from '../utils/validation';

// Interface for the form data
interface FormData {
  patientInfo: {
    patientType: string;
    firstName: string;
    lastName: string;
    nationalId: string;
    mobile: string;
    gender: string;
  };
  samplingLocation: {
    type: string; // 'home' or 'lab'
  };
  addressInfo: {
    region: string;
    field: string; // میدان
    street: string;
    alley: string;
    building: string;
    unit: string;
    number: string; // پلاک
    coordinates?: [number, number]; // Latitude and longitude coordinates
  };
  ageInfo: {
    age: string;
  };
  insuranceInfo: {
    type: string; // 'free', 'paper', 'electronic', 'package'
    nationalIdImage?: File | null;
    nationalIdImageUrl?: string;
    testListImage?: File | null;
    testListImageUrl?: string;
    prescriptionImage?: File | null;
    prescriptionImageUrl?: string;
    insuranceBookletImage?: File | null;
    insuranceBookletImageUrl?: string;
    trackingCode?: string;
    description?: string;
    basicInsurance?: string;
    supplementaryInsurance?: string;
    selectedPackage?: string;
  };
}

const NewTestRequest: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id; // If id exists, we're in edit mode
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [familyData, setFamilyData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingRequest, setLoadingRequest] = useState<boolean>(isEditMode);
  const [originalRequestStatus, setOriginalRequestStatus] = useState<string | null>(null);
  const [draftCreated, setDraftCreated] = useState<boolean>(isEditMode); // Track if draft has been created
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState<boolean>(false); // Track if Next button should be disabled
  const [countdownTimer, setCountdownTimer] = useState<number>(0); // Track countdown timer
  const [regions, setRegions] = useState<Region[]>([]);
  const [regionsLoading, setRegionsLoading] = useState<boolean>(true);
  const [regionsError, setRegionsError] = useState<string>('');
  const [testPackages, setTestPackages] = useState<TestPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState<boolean>(true);
  const [packagesError, setPackagesError] = useState<string>('');
  const [insurances, setInsurances] = useState<Insurance[]>([]);
  const [insurancesLoading, setInsurancesLoading] = useState<boolean>(true);
  const [insurancesError, setInsurancesError] = useState<string>('');
  const [supplementaryInsurances, setSupplementaryInsurances] = useState<SupplementaryInsurance[]>([]);
  const [supplementaryInsurancesLoading, setSupplementaryInsurancesLoading] = useState<boolean>(true);
  const [supplementaryInsurancesError, setSupplementaryInsurancesError] = useState<string>('');
  const [messageCenter, setMessageCenter] = useState<MessageCenterData | null>(null);
  const [showCancelButton, setShowCancelButton] = useState<boolean>(false);

  const [formData, setFormData] = useState<FormData>({
    patientInfo: {
      patientType: 'self', // Default to 'self' to avoid validation errors
      firstName: '',
      lastName: '',
      nationalId: '',
      mobile: '',
      gender: '',
    },
    samplingLocation: {
      type: 'home', // Default to home sampling
    },
    addressInfo: {
      region: '',
      field: '',
      street: '',
      alley: '',
      building: '',
      unit: '',
      number: '',
      coordinates: [49.59, 37.28], // Default center for Rasht
    },
    ageInfo: {
      age: '',
    },
    insuranceInfo: {
      type: '',
      nationalIdImage: null,
      testListImage: null,
      prescriptionImage: null,
      insuranceBookletImage: null,
      trackingCode: '',
      description: '',
      basicInsurance: '',
      supplementaryInsurance: '',
      selectedPackage: '',
    },
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const location = useLocation();
  const initialFormDataRef = useRef<FormData>(formData);

  useEffect(() => {
    setCurrentStep(1);
    setFormData(initialFormDataRef.current);
    setErrors({});
  }, [location.key]);

  // Load user profile and family data when component mounts.
  useEffect(() => {
    const loadDataAndPopulateForm = async () => {
      try {
        setLoading(true);
        let fetchedUserProfile: any = null;

        // Load user profile data
        const userResponse = await getUserProfile();
        if (userResponse.success && userResponse.data) {
          fetchedUserProfile = userResponse.data;
          setUserProfile(fetchedUserProfile);
        }

        // Load family data
        const familyResponse = await getFamily();
        if (familyResponse.success && familyResponse.data) {
          setFamilyData(familyResponse.data);
        }
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    loadDataAndPopulateForm();
  }, []);

  // Populate form with user profile data when userProfile is loaded and patientType is 'self'
  useEffect(() => {
    if (userProfile && formData.patientInfo.patientType === 'self') {

      setFormData(prev => ({
        ...prev,
        patientInfo: {
          ...prev.patientInfo,
          firstName: userProfile.name || '',
          lastName: userProfile.lastName || '',
          nationalId: userProfile.nationalId || '',
          mobile: userProfile.mobile || '',
          gender: userProfile.gender || ''
        }
      }));
    }
  }, [userProfile]);

  // Debug form data changes
  useEffect(() => {

  }, [formData.patientInfo]);

  // Load existing request data when in edit mode
  useEffect(() => {
    const loadRequestData = async () => {
      if (!isEditMode || !id) return;

      try {
        setLoadingRequest(true);
        const token = localStorage.getItem('authToken');

        if (!token) {
          toast.error('لطفا ابتدا وارد حساب کاربری خود شوید');
          navigate('/login');
          return;
        }

        const response = await fetch(API_CONFIG.getUrl(`test-requests/${id}/edit`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'خطا در دریافت اطلاعات درخواست');
        }

        const data = await response.json();
        const request = data.data;

        // Store the original request status
        setOriginalRequestStatus(request.status || null);

        // Populate form with existing data
        setFormData({
          patientInfo: {
            patientType: request.patientInfo?.patientType || 'self',
            firstName: request.patientInfo?.firstName || '',
            lastName: request.patientInfo?.lastName || '',
            nationalId: request.patientInfo?.nationalId || '',
            mobile: request.patientInfo?.mobile || '',
            gender: request.patientInfo?.gender || '',
          },
          samplingLocation: {
            type: request.samplingLocation?.type || 'home',
          },
          addressInfo: {
            region: request.addressInfo?.region || '',
            field: request.addressInfo?.field || '',
            street: request.addressInfo?.street || '',
            alley: request.addressInfo?.alley || '',
            building: request.addressInfo?.building || '',
            unit: request.addressInfo?.unit || '',
            number: request.addressInfo?.number || '',
            coordinates: request.addressInfo?.coordinates || [49.59, 37.28],
          },
          ageInfo: {
            age: request.ageInfo?.age?.toString() || '',
          },
          insuranceInfo: {
            type: request.insuranceInfo?.type || '',
            nationalIdImage: null,
            nationalIdImageUrl: request.insuranceInfo?.nationalIdImageUrl || '',
            testListImage: null,
            testListImageUrl: request.insuranceInfo?.testListImageUrl || '',
            prescriptionImage: null,
            prescriptionImageUrl: request.insuranceInfo?.prescriptionImageUrl || '',
            insuranceBookletImage: null,
            insuranceBookletImageUrl: request.insuranceInfo?.insuranceBookletImageUrl || '',
            trackingCode: request.insuranceInfo?.trackingCode || '',
            description: request.insuranceInfo?.description || '',
            basicInsurance: request.insuranceInfo?.basicInsurance || '',
            supplementaryInsurance: request.insuranceInfo?.supplementaryInsurance || '',
            selectedPackage: request.insuranceInfo?.selectedPackage || '',
          },
        });

      } catch (error: any) {

        toast.error(error.message || 'خطا در دریافت اطلاعات درخواست');
        navigate('/my-tests');
      } finally {
        setLoadingRequest(false);
      }
    };

    loadRequestData();
  }, [isEditMode, id, navigate]);

  // Load regions when component mounts
  useEffect(() => {
    const loadRegions = async () => {
      try {
        setRegionsLoading(true);
        setRegionsError('');
        const activeRegions = await getActiveRegions();
        setRegions(activeRegions);
      } catch (error) {

        setRegionsError('خطا در بارگیری لیست مناطق');
        // Fallback to default regions if API fails

      } finally {
        setRegionsLoading(false);
      }
    };

    loadRegions();
  }, []); // Runs once on mount

  // Load test packages when component mounts
  useEffect(() => {
    const loadTestPackages = async () => {
      try {
        setPackagesLoading(true);
        setPackagesError('');
        const activePackages = await getActiveTestPackages();
        setTestPackages(activePackages);
      } catch (error) {

        setPackagesError('خطا در بارگیری لیست پکیج ها');
        // Fallback to default packages if API fails
        setTestPackages([
          {
            _id: 'checkup1',
            name: 'چکاپ عمومی',
            description: 'آزمایشات عمومی پایه',
            price: 500000,
            discountPrice: 450000,
            category: 'common',
            priority: 1,
            active: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          {
            _id: 'checkup2',
            name: 'چکاپ تخصصی',
            description: 'آزمایشات تخصصی کامل',
            price: 800000,
            discountPrice: 720000,
            category: 'specialized',
            priority: 2,
            active: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          {
            _id: 'covid',
            name: 'تست کووید',
            description: 'آزمایش کووید-19',
            price: 200000,
            discountPrice: 180000,
            category: 'emergency',
            priority: 3,
            active: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        ]);
      } finally {
        setPackagesLoading(false);
      }
    };

    loadTestPackages();
  }, []); // Runs once on mount

  // Load insurances when component mounts
  useEffect(() => {
    const loadInsurances = async () => {
      try {
        setInsurancesLoading(true);
        setInsurancesError('');
        const activeInsurances = await getActiveInsurances();
        setInsurances(activeInsurances);
      } catch (error) {

        setInsurancesError('خطا در بارگیری لیست بیمه ها');
        // Fallback to default insurances if API fails

      } finally {
        setInsurancesLoading(false);
      }
    };

    loadInsurances();
  }, []); // Runs once on mount

  // Load supplementary insurances when component mounts
  useEffect(() => {
    const loadSupplementaryInsurances = async () => {
      try {
        setSupplementaryInsurancesLoading(true);
        setSupplementaryInsurancesError('');
        const activeSupplementaryInsurances = await getActiveSupplementaryInsurances();
        setSupplementaryInsurances(activeSupplementaryInsurances);
      } catch (error) {

        setSupplementaryInsurancesError('خطا در بارگیری لیست بیمه های تکمیلی');
        // Fallback to default supplementary insurances if API fails

      } finally {
        setSupplementaryInsurancesLoading(false);
      }
    };

    loadSupplementaryInsurances();
  }, []); // Runs once on mount

  // Load message center data when component mounts
  useEffect(() => {
    const loadMessageCenter = async () => {
      try {
        const response = await getMessages();
        if (response.success) {
          setMessageCenter(response.data);
        }
      } catch (error) {

        // Continue without message center data
      }
    };

    loadMessageCenter();
  }, []); // Runs once on mount

  // Clear insurance code when insurance selection changes or doesn't require extra code
  useEffect(() => {
    if (formData.insuranceInfo.basicInsurance && formData.insuranceInfo.basicInsurance !== 'none') {
      const selectedInsurance = insurances.find(ins => ins._id === formData.insuranceInfo.basicInsurance);
      // Clear tracking code for non-electronic types
      if (formData.insuranceInfo.trackingCode && formData.insuranceInfo.type !== 'electronic') {
        handleFormDataChange('insuranceInfo', 'trackingCode', '');
      }
    } else {
      // Clear tracking code if no insurance is selected or "none" is selected
      if (formData.insuranceInfo.trackingCode) {
        handleFormDataChange('insuranceInfo', 'trackingCode', '');
      }
    }
  }, [formData.insuranceInfo.basicInsurance, insurances]);

  useEffect(() => {
    const { patientType } = formData.patientInfo;
    if (patientType === 'self' && userProfile) {
      setFormData(prev => ({
        ...prev,
        patientInfo: {
          ...prev.patientInfo,
          firstName: userProfile.name || '',
          lastName: userProfile.lastName || '',
          nationalId: userProfile.nationalId || '',
          mobile: userProfile.mobile || '', // Populate mobile from user profile for "self"
        }
      }));
    } else if (patientType !== 'other' && familyData) {
      // Only auto-populate for family members, not for "other"
      let memberData: any = null;
      if (patientType === 'spouse') memberData = familyData.spouse;
      else if (patientType === 'child') memberData = familyData.children;
      else if (patientType === 'father') memberData = familyData.father;
      else if (patientType === 'mother') memberData = familyData.mother;
      if (memberData) {
        setFormData(prev => ({
          ...prev,
          patientInfo: {
            ...prev.patientInfo,
            firstName: memberData.firstName || '',
            lastName: memberData.lastName || '',
            nationalId: memberData.nationalId || '',
            mobile: memberData.mobile || '', // Populate mobile from family data
          }
        }));
      } else {
        // Only clear fields for family member types that don't have data yet
        // Don't clear for "other" type
        setFormData(prev => ({
          ...prev,
          patientInfo: {
            ...prev.patientInfo,
            firstName: '',
            lastName: '',
            nationalId: '',
            mobile: '', // Clear mobile when no family data
          }
        }));
      }
    }
    // If patientType is 'other', don't auto-populate or clear - keep existing data
  }, [formData.patientInfo.patientType, userProfile, familyData]);

  useEffect(() => {
    const { patientType } = formData.patientInfo;
    // Force gender for parents
    if (patientType === 'father') {
      handleFormDataChange('patientInfo', 'gender', 'male');
    } else if (patientType === 'mother') {
      handleFormDataChange('patientInfo', 'gender', 'female');
    }
  }, [formData.patientInfo.patientType]);

  // Effect to handle cancel button visibility based on region selection
  useEffect(() => {
    if (formData.addressInfo.region === 'سایر مناطق') {
      setShowCancelButton(true);
    } else {
      setShowCancelButton(false);
    }
  }, [formData.addressInfo.region]);

  // Step titles for the step indicator
  const stepTitles = {
    1: 'مشخصات بیمار',
    2: 'محل نمونه گیری',
    3: 'سن بیمار',
    4: 'بیمه',
    5: 'بررسی نهایی'
  };

  const handleFormDataChange = (section: keyof FormData, field: string, value: any) => {
    let processedValue = value;

    // Apply input formatting and validation for specific fields
    if (section === 'patientInfo') {
      if (field === 'mobile') {
        // Clean and format mobile number - keep only digits
        processedValue = cleanDigitsOnly(value);
      } else if (field === 'nationalId') {
        // Clean and format national ID - keep only digits and limit to 10
        processedValue = cleanDigitsOnly(value).slice(0, 10);
      }
    }

    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: processedValue,
      },
    });

    // Clear error for this field if it exists
    if (errors[`${section}.${field}`]) {
      setErrors({
        ...errors,
        [`${section}.${field}`]: '',
      });
    }

    // If patient type is "self" and we're changing patientType, populate from user profile including mobile
    if (section === 'patientInfo' && field === 'patientType' && value === 'self') {
      if (userProfile) {

        setFormData(prev => ({
          ...prev,
          patientInfo: {
            ...prev.patientInfo,
            firstName: userProfile.name || '',
            lastName: userProfile.lastName || '',
            nationalId: userProfile.nationalId || '',
            mobile: userProfile.mobile || '', // Populate mobile from user profile for "self"
            gender: userProfile.gender || ''
          }
        }));
      }
    }
  };

  // Render step indicator with responsive design
  const renderStepIndicator = () => {
    return (
      <div className="relative flex justify-between mb-8">
        {/* Progress line behind the steps */}
        <div className="absolute top-4 md:top-5 left-0 right-0 h-1 bg-slate-200 -z-10">
          <div
            className={`h-full bg-green-500 transition-all duration-300 ${currentStep === 1 ? 'w-0' :
              currentStep === 2 ? 'w-1/4' :
                currentStep === 3 ? 'w-2/4' :
                  currentStep === 4 ? 'w-3/4' :
                    'w-full'}`}
          />
        </div>

        {[1, 2, 3, 4, 5].map((step) => {
          // Define variables for step state
          const isActive = step === currentStep;
          const isCompleted = step < currentStep;

          return (
            <div key={step} className="flex flex-col items-center relative group">
              <div
                className={`
                  flex items-center justify-center rounded-full transition-all duration-200
                  md:h-10 md:w-10 h-8 w-8
                  ${isActive ? 'bg-blue-600 text-white shadow-md' :
                    isCompleted ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-700'}
                `}
                aria-current={isActive ? 'step' : undefined}
              >
                {isCompleted ? '✓' : step}
              </div>

              {/* Step title - hidden on mobile, visible on medium screens and up */}
              <div className="md:block hidden text-sm mt-2 text-center">
                {stepTitles[step as keyof typeof stepTitles]}
              </div>

              {/* Mobile tooltip that shows on hover/touch */}
              <div className="md:hidden block">
                <div
                  className="
                    invisible group-hover:visible opacity-0 group-hover:opacity-100 
                    absolute top-full mt-1 left-1/2 -translate-x-1/2 
                    bg-slate-800 text-white text-xs rounded py-1 px-2 
                    transition-opacity duration-200 whitespace-nowrap
                    pointer-events-none z-20
                  "
                  aria-hidden="true"
                >
                  {stepTitles[step as keyof typeof stepTitles]}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    switch (currentStep) {
      case 1: // Validate Patient Info
        if (!formData.patientInfo.patientType) {
          newErrors['patientInfo.patientType'] = 'لطفا نسبت بیمار را انتخاب کنید';
        }
        if (!formData.patientInfo.firstName) {
          newErrors['patientInfo.firstName'] = 'نام بیمار الزامی است';
        }
        if (!formData.patientInfo.lastName) {
          newErrors['patientInfo.lastName'] = 'نام خانوادگی بیمار الزامی است';
        }
        // Validate national ID
        const nationalIdValidation = validateIranianNationalCode(formData.patientInfo.nationalId);
        if (!nationalIdValidation.isValid) {
          newErrors['patientInfo.nationalId'] = nationalIdValidation.message;
        }

        // Validate mobile number
        const mobileValidation = validateMobileNumber(formData.patientInfo.mobile);
        if (!mobileValidation.isValid) {
          newErrors['patientInfo.mobile'] = mobileValidation.message;
        }
        if (!formData.patientInfo.gender) {
          newErrors['patientInfo.gender'] = 'جنسیت بیمار را انتخاب کنید';
        }
        break;
      case 2: // Validate Sampling Location and Address Info
        if (!formData.samplingLocation.type) {
          newErrors['samplingLocation.type'] = 'لطفا محل نمونه گیری را انتخاب کنید';
        }

        // Only validate address fields if home sampling is selected
        if (formData.samplingLocation.type === 'home') {
          if (!formData.addressInfo.region) {
            newErrors['addressInfo.region'] = 'لطفا منطقه را انتخاب کنید';
          } else if (formData.addressInfo.region === 'سایر مناطق') {
            // Show MX4 message and prevent proceeding
            const mx4Message = messageCenter?.messageCenter?.MX4 || 'متأسفانه منطقه انتخابی شما تحت پوشش نمونه‌گیری در منزل نمی‌باشد.';
            toast.error(mx4Message);
            newErrors['addressInfo.region'] = 'منطقه انتخابی تحت پوشش نمونه‌گیری در منزل نمی باشد نمونه گیری در آزمایشگاه را انتخاب کنید ';
          }
          if (!formData.addressInfo.field) {
            newErrors['addressInfo.field'] = 'میدان الزامی است';
          }
          if (!formData.addressInfo.street) {
            newErrors['addressInfo.street'] = 'خیابان الزامی است';
          }
        }
        break;
      case 3: // Validate Age Info
        if (!formData.ageInfo.age) {
          newErrors['ageInfo.age'] = 'سن بیمار الزامی است';
        } else if (parseInt(formData.ageInfo.age) < 5) {

          // Show error message
          const mx5Message = messageCenter?.messageCenter?.MX5 || 'نمونه‌گیری در منزل برای افراد زیر 5 سال امکان‌پذیر نمی‌باشد.';
          toast.error(mx5Message);
          newErrors['ageInfo.age'] = 'سن بیمار باید حداقل 5 سال باشد';

          // Different behavior for save mode vs edit mode
          if (!isEditMode) {
            // SAVE MODE: Disable Next button for 3 seconds, then switch to edit mode

            setIsNextButtonDisabled(true);
            setCountdownTimer(3);
            
            // Start countdown timer
            const countdownInterval = setInterval(() => {
              setCountdownTimer((prev) => {
                if (prev <= 1) {
                  clearInterval(countdownInterval);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
            
            // Auto-save draft when age validation fails - but only if we haven't created one yet
            if (!draftCreated) {

              const saveDraftWithInvalidAge = async () => {
            try {
              const token = localStorage.getItem('authToken');

              if (!token) {

                return;
              }

              // Create request data keeping the invalid age for debugging
              const requestData = {
                ...formData,
                ageInfo: {
                  ...formData.ageInfo,
                  age: formData.ageInfo.age // Keep the invalid age as entered by user
                },
                status: 'incomplete'
              };

              const endpoint = isEditMode ? `/test-requests/${id}` : '/test-requests';
              const method = isEditMode ? 'PUT' : 'POST';

              const response = await fetch(API_CONFIG.getUrl(endpoint), {
                method: method,
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(requestData),
              });

              if (response.ok) {
                const responseData = await response.json();
                toast.success('اطلاعات شما به صورت خودکار ذخیره شد (سن نامعتبر: ' + formData.ageInfo.age + ')');

                if (!isEditMode && responseData.data?._id) {
                  // Set draft as created
                  setDraftCreated(true);
                  
                  // After 3 seconds, switch to edit mode and re-enable button
                  setTimeout(() => {
                    window.history.replaceState(null, '', `/edit-request/${responseData.data._id}`);
                    setIsNextButtonDisabled(false);
                    setCountdownTimer(0);
                    window.location.reload(); // Reload to update the id in useParams
                  }, 3000);
                }

                return true;
              } else {
                const errorData = await response.json();

                return false;
              }
            } catch (error) {

              return false;
            }
          };

              // Execute the save
              saveDraftWithInvalidAge();
            } else {

              // Still disable button for 3 seconds even if draft already exists
              setTimeout(() => {
                setIsNextButtonDisabled(false);
                setCountdownTimer(0);
              }, 3000);
            }
          } else {
            // EDIT MODE: Just show error, don't save anything

          }

        }
        break;
      case 4: // Validate Insurance Info
        if (!formData.insuranceInfo.type) {
          newErrors['insuranceInfo.type'] = 'نوع درخواست آزمایش را انتخاب کنید';
        }

        if (formData.insuranceInfo.type === 'free') {
          // Required files for free type
          if (!formData.insuranceInfo.nationalIdImage && !formData.insuranceInfo.nationalIdImageUrl) {
            newErrors['insuranceInfo.nationalIdImage'] = 'آپلود تصویر کارت ملی الزامی است';
          }
          if (!formData.insuranceInfo.description) {
            newErrors['insuranceInfo.description'] = 'توضیحات برای تعیین نوع تست الزامی است';
          }
        }

        if (formData.insuranceInfo.type === 'paper') {
          // Required fields for paper type
          if (!formData.insuranceInfo.basicInsurance) {
            newErrors['insuranceInfo.basicInsurance'] = 'انتخاب بیمه پایه الزامی است';
          } else if (formData.insuranceInfo.basicInsurance !== 'none') {
            // Check if selected insurance requires extra code (only for non-none selections)
            const selectedInsurance = insurances.find(ins => ins._id === formData.insuranceInfo.basicInsurance);
            // Check if selected insurance requires tracking code for electronic prescriptions
            if ((formData.insuranceInfo.type as string) === 'electronic' && selectedInsurance?.requiresExtraCode && !formData.insuranceInfo.trackingCode) {
              newErrors['insuranceInfo.trackingCode'] = 'کد رهگیری نسخه الکترونیک الزامی است';
            }
            // Check if selected insurance requires insurance booklet
            if (selectedInsurance?.requiresInsuranceBooklet && !formData.insuranceInfo.insuranceBookletImage && !formData.insuranceInfo.insuranceBookletImageUrl) {
              newErrors['insuranceInfo.insuranceBookletImage'] = 'آپلود تصویر دفترچه (صفحه اول) بیمه الزامی است';
            }
          }
          // Supplementary insurance is required for paper type
          if (!formData.insuranceInfo.supplementaryInsurance) {
            newErrors['insuranceInfo.supplementaryInsurance'] = 'انتخاب بیمه تکمیلی الزامی است';
          }
          // Required files for paper type
          if (!formData.insuranceInfo.nationalIdImage && !formData.insuranceInfo.nationalIdImageUrl) {
            newErrors['insuranceInfo.nationalIdImage'] = 'آپلود تصویر کارت ملی الزامی است';
          }
          if (!formData.insuranceInfo.prescriptionImage && !formData.insuranceInfo.prescriptionImageUrl) {
            newErrors['insuranceInfo.prescriptionImage'] = 'آپلود تصویر نسخه پزشک الزامی است';
          }
        }

        if ((formData.insuranceInfo.type as string) === 'electronic') {
          // Required fields for electronic type
          if (!formData.insuranceInfo.basicInsurance) {
            newErrors['insuranceInfo.basicInsurance'] = 'انتخاب بیمه پایه الزامی است';
          } else if (formData.insuranceInfo.basicInsurance !== 'none') {
            // Check if selected insurance requires tracking code for electronic prescriptions
            const selectedInsurance = insurances.find(ins => ins._id === formData.insuranceInfo.basicInsurance);
            if (selectedInsurance?.requiresExtraCode && !formData.insuranceInfo.trackingCode) {
              newErrors['insuranceInfo.trackingCode'] = 'کد رهگیری نسخه الکترونیک الزامی است';
            }
            // Check if selected insurance requires insurance booklet
            if (selectedInsurance?.requiresInsuranceBooklet && !formData.insuranceInfo.insuranceBookletImage && !formData.insuranceInfo.insuranceBookletImageUrl) {
              newErrors['insuranceInfo.insuranceBookletImage'] = 'آپلود تصویر دفترچه (صفحه اول) بیمه الزامی است';
            }
          }
          // Supplementary insurance is required for electronic type
          if (!formData.insuranceInfo.supplementaryInsurance) {
            newErrors['insuranceInfo.supplementaryInsurance'] = 'انتخاب بیمه تکمیلی الزامی است';
          }
          // Required files for electronic type
          if (!formData.insuranceInfo.nationalIdImage && !formData.insuranceInfo.nationalIdImageUrl) {
            newErrors['insuranceInfo.nationalIdImage'] = 'آپلود تصویر کارت ملی الزامی است';
          }
        }

        if (formData.insuranceInfo.type === 'package') {
          // Required fields for package type
          if (!formData.insuranceInfo.selectedPackage) {
            newErrors['insuranceInfo.selectedPackage'] = 'انتخاب پکیج الزامی است';
          }
          // Required files for package type
          if (!formData.insuranceInfo.nationalIdImage && !formData.insuranceInfo.nationalIdImageUrl) {
            newErrors['insuranceInfo.nationalIdImage'] = 'آپلود تصویر کارت ملی الزامی است';
          }
        }
        break;
    }

    setErrors(newErrors);

    // Scroll to the first error after a short delay to allow for DOM updates
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        const firstErrorKey = Object.keys(newErrors)[0];
        const errorElement = document.querySelector(`[data-error="${firstErrorKey}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Fallback: Try to find error message
          const errorMessage = document.querySelector(`.text-red-500`);
          if (errorMessage) {
            errorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
    }

    return Object.keys(newErrors).length === 0;
  };

  const saveDraftInternal = async (isAutoSave = false) => {
    try {
      // Get the authentication token from localStorage
      const token = localStorage.getItem('authToken');

      if (!token) {
        if (!isAutoSave) {
          toast.error('لطفا ابتدا وارد حساب کاربری خود شوید');
        }
        return;
      }

      // Prepare the form data for draft submission with incomplete status
      const requestData = {
        ...formData,
        status: 'incomplete'
      };

      // Determine endpoint and method based on mode
      const endpoint = isEditMode ? `/test-requests/${id}` : '/test-requests';
      const method = isEditMode ? 'PUT' : 'POST';

      // Save draft to database with authentication token
      const response = await fetch(API_CONFIG.getUrl(endpoint), {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const responseData = await response.json();
        const successMessage = isAutoSave
          ? 'اطلاعات شما به صورت خودکار ذخیره شد'
          : isEditMode
            ? 'پیش‌نویس با موفقیت ذخیره شد'
            : 'پیش‌نویس درخواست با موفقیت ذخیره شد';
        toast.success(successMessage);

        // If this is a new request, update the URL to edit mode
        if (!isEditMode && responseData.data?._id) {
          window.history.replaceState(null, '', `/edit-request/${responseData.data._id}`);
          setDraftCreated(true);
        }

        return true;
      } else {
        // Handle error from server
        const errorData = await response.json();
        if (!isAutoSave) {
          toast.error(`خطا: ${errorData.message}`);
        }
        return false;
      }
    } catch (error) {
      // Handle network error

      if (!isAutoSave) {
        const errorMessage = 'خطا در ذخیره پیش‌نویس. لطفا دوباره تلاش کنید.';
        toast.error(errorMessage);
      }
      return false;
    }
  };

  const saveDraft = () => saveDraftInternal(false);

  const nextStep = async () => {
    if (validateStep()) {
      // If moving from step 1 to step 2, save information to user profile and family data
      if (currentStep === 1 && formData.patientInfo.patientType) {
        try {
          // Get the selected patient type
          const type = formData.patientInfo.patientType;

          // Save user profile data for "self" - excluding mobile number
          if (type === 'self' && (formData.patientInfo.nationalId || formData.patientInfo.gender)) {
            const profileUpdateData = new FormData();
            if (formData.patientInfo.nationalId) {
              profileUpdateData.append('nationalId', formData.patientInfo.nationalId);
            }
            if (formData.patientInfo.gender) {
              profileUpdateData.append('gender', formData.patientInfo.gender);
            }
            // Note: Mobile number is NOT saved for user's own profile

            const profileResponse = await updateUserProfile(profileUpdateData);
            if (profileResponse.success) {

              toast.success('اطلاعات پروفایل ذخیره شد');
            } else {

              toast.error(profileResponse.message || 'خطا در به‌روزرسانی پروفایل');
            }
          }

          // Save family data for family members (not self or other)
          // Save even if some fields are empty to update existing data
          if (type !== 'self' && type !== 'other') {
            // Create family data object
            let familyData = {};

            // Build the specific family member object based on relationship type
            // Note: Mobile number IS saved for family members
            if (type === 'spouse') {
              familyData = {
                spouse: {
                  firstName: formData.patientInfo.firstName || '',
                  lastName: formData.patientInfo.lastName || '',
                  nationalId: formData.patientInfo.nationalId || '',
                  mobile: formData.patientInfo.mobile || '',
                  gender: formData.patientInfo.gender || ''
                }
              };
            } else if (type === 'child') {
              familyData = {
                children: {
                  firstName: formData.patientInfo.firstName || '',
                  lastName: formData.patientInfo.lastName || '',
                  nationalId: formData.patientInfo.nationalId || '',
                  mobile: formData.patientInfo.mobile || '',
                  gender: formData.patientInfo.gender || '',
                  birthdate: null // Could add birthdate field in future
                }
              };
            } else if (type === 'father') {
              familyData = {
                father: {
                  firstName: formData.patientInfo.firstName || '',
                  lastName: formData.patientInfo.lastName || '',
                  nationalId: formData.patientInfo.nationalId || '',
                  mobile: formData.patientInfo.mobile || '',
                  gender: formData.patientInfo.gender || ''
                }
              };
            } else if (type === 'mother') {
              familyData = {
                mother: {
                  firstName: formData.patientInfo.firstName || '',
                  lastName: formData.patientInfo.lastName || '',
                  nationalId: formData.patientInfo.nationalId || '',
                  mobile: formData.patientInfo.mobile || '',
                  gender: formData.patientInfo.gender || ''
                }
              };
            }

            // Always make the API call to save family data (even if some fields are empty)
            if (Object.keys(familyData).length > 0) {
              try {
                const familyResponse = await updateFamily(familyData);
                if (familyResponse.success) {

                  toast.success('اطلاعات عضو خانواده ذخیره شد');
                } else {

                  toast.error(familyResponse.message || 'خطا در ذخیره اطلاعات عضو خانواده');
                }
              } catch (familyError) {

                toast.error('خطا در ذخیره اطلاعات عضو خانواده');
              }
            }
          }
        } catch (error) {

          // Continue to next step even if there's an error with family data update
        }
      }

      // Skip age step for lab sampling: go directly to insurance step
      if (currentStep === 2 && formData.samplingLocation.type === 'lab') {
        setCurrentStep(4);
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    // Skip age step when going back from insurance for lab sampling
    if (currentStep === 4 && formData.samplingLocation.type === 'lab') {
      setCurrentStep(2);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (validateStep()) {
      try {
        // Get the authentication token from localStorage
        const token = localStorage.getItem('authToken');

        if (!token) {
          toast.error('لطفا ابتدا وارد حساب کاربری خود شوید');
          return;
        }

        // If patient type is "self", save nationalId and gender to user profile (excluding mobile)
        if (formData.patientInfo.patientType === 'self') {
          try {
            const profileUpdateData = new FormData();
            if (formData.patientInfo.nationalId) {
              profileUpdateData.append('nationalId', formData.patientInfo.nationalId);
            }
            if (formData.patientInfo.gender) {
              profileUpdateData.append('gender', formData.patientInfo.gender);
            }
            // Note: Mobile number is NOT saved for user's own profile

            // Only update if there's data to update
            if (formData.patientInfo.nationalId || formData.patientInfo.gender) {
              const profileResponse = await updateUserProfile(profileUpdateData);
              if (profileResponse.success) {

                toast.success('اطلاعات پروفایل به‌روزرسانی شد');
              } else {

                toast.error(profileResponse.message || 'خطا در به‌روزرسانی پروفایل');
              }
            }
          } catch (profileError: any) {

            if (profileError.response?.data?.message) {
              toast.error(profileError.response.data.message);
            } else {
              toast.error('خطا در به‌روزرسانی پروفایل');
            }
            // Don't fail the main submission if profile update fails
          }
        } else if (formData.patientInfo.patientType !== 'other') {
          // If family member is selected, save their data to family info
          try {
            const familyUpdateData = {
              [formData.patientInfo.patientType]: {
                firstName: formData.patientInfo.firstName,
                lastName: formData.patientInfo.lastName,
                nationalId: formData.patientInfo.nationalId,
                mobile: formData.patientInfo.mobile, // Mobile number IS saved for family members
                gender: formData.patientInfo.gender,
                birthdate: formData.ageInfo.age ? new Date(new Date().getFullYear() - parseInt(formData.ageInfo.age), 0, 1).toISOString() : undefined
              }
            };

            const familyResponse = await updateFamily(familyUpdateData);
            if (familyResponse.success) {

              toast.success('اطلاعات عضو خانواده به‌روزرسانی شد');
            } else {

              toast.error(familyResponse.message || 'خطا در به‌روزرسانی اطلاعات عضو خانواده');
            }
          } catch (familyError) {

            toast.error('خطا در به‌روزرسانی اطلاعات عضو خانواده');
            // Don't fail the main submission if family update fails
          }
        }

        // Prepare the form data for submission
        // Ensure insurance info is properly formatted and marked as complete
        const insuranceInfo = formData.insuranceInfo || {};

        const requestData = {
          ...formData,
          status: 'reviewing', // Set status to reviewing for final submission
          insuranceInfo: {
            ...insuranceInfo,
            type: insuranceInfo.type || 'free', // Default to 'free' if not specified
            isComplete: true  // Add this flag to mark insurance info as complete
          }
        };

        // Determine endpoint and method based on mode
        const endpoint = isEditMode ? `/test-requests/${id}` : '/test-requests';
        const method = isEditMode ? 'PUT' : 'POST';

        // Save or update test request to database with authentication token
        const response = await fetch(API_CONFIG.getUrl(endpoint), {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(requestData),
        });

        if (response.ok) {
          // Handle success - show success message and redirect
          const successMessage = isEditMode && originalRequestStatus !== 'incomplete'
            ? 'درخواست با موفقیت ویرایش شد'
            : 'درخواست آزمایش با موفقیت ثبت شد';
          toast.success(successMessage);

          // Redirect to my-tests page
          navigate('/my-tests');
        } else {
          // Handle error from server
          const errorData = await response.json();
          toast.error(`خطا: ${errorData.message}`);
        }
      } catch (error) {
        // Handle network error

        const errorMessage = isEditMode && originalRequestStatus !== 'incomplete'
          ? 'خطا در ویرایش درخواست. لطفا دوباره تلاش کنید.'
          : 'خطا در ارسال درخواست. لطفا دوباره تلاش کنید.';
        toast.error(errorMessage);
      }
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderPatientInfoStep();
      case 2:
        return renderAddressStep();
      case 3:
        return renderAgeStep();
      case 4:
        return renderInsuranceStep();
      case 5:
        return renderReviewStep();
      default:
        return null;
    }
  };

  // Render review step
  const renderReviewStep = () => {
    // Helper function to render uploaded image preview
    const renderImagePreview = (imageUrl: string | undefined, title: string) => {
      if (!imageUrl) return null;

      return (
        <div className="mb-4">
          <h5 className="font-medium text-sm mb-2">{title}</h5>
          <div className="border border-slate-300 rounded-sm p-2 bg-slate-50">
            <img
              src={`${API_CONFIG.uploadURL}${imageUrl}`}
              alt={title}
              className="max-h-40 object-contain mx-auto"
            />
          </div>
        </div>
      );
    };

    // Build insurance type display name
    const getInsuranceTypeDisplay = () => {
      const insuranceType = formData.insuranceInfo.type;
      switch (insuranceType) {
        case 'free': return 'آزاد';
        case 'paper': return 'دفترچه بیمه';
        case 'electronic': return 'نسخه الکترونیک';
        case 'package': return 'پکیج آزمایشگاه';
        default: return '';
      }
    };

    // Get basic insurance display name
    const getBasicInsuranceDisplay = () => {
      if (!formData.insuranceInfo.basicInsurance) return '';
      if (formData.insuranceInfo.basicInsurance === 'none') return 'هیچکدام';

      const found = insurances.find(insurance => insurance._id === formData.insuranceInfo.basicInsurance);
      return found ? found.name : 'حذف شده';
    };

    // Get supplementary insurance display
    const getSupplementaryInsuranceDisplay = () => {
      if (!formData.insuranceInfo.supplementaryInsurance) return '';
      if (formData.insuranceInfo.supplementaryInsurance === 'none') return 'هیچکدام';
      
      const found = supplementaryInsurances.find(insurance => insurance._id === formData.insuranceInfo.supplementaryInsurance);
      return found ? found.name : 'حذف شده';
    };

    return (
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <h3 className="text-lg font-semibold mb-6 text-center">بررسی نهایی اطلاعات</h3>
        <p className="text-slate-600 mb-6 text-center">
          لطفا اطلاعات وارد شده را بررسی کنید و در صورت نیاز به تغییر، به مرحله مربوطه بازگردید.
        </p>

        {/* Patient Info Section */}
        <div className="mb-8 border-b pb-4">
          <h4 className="font-bold text-blue-700 mb-4">مشخصات بیمار</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex">
              <span className="font-medium ml-2">نام و نام خانوادگی:</span>
              <span>{`${formData.patientInfo.firstName} ${formData.patientInfo.lastName}`}</span>
            </div>
            <div className="flex">
              <span className="font-medium ml-2">کد ملی:</span>
              <span>{formData.patientInfo.nationalId}</span>
            </div>
            <div className="flex">
              <span className="font-medium ml-2">شماره موبایل:</span>
              <span>{formData.patientInfo.mobile}</span>
            </div>
            <div className="flex">
              <span className="font-medium ml-2">جنسیت:</span>
              <span>{formData.patientInfo.gender === 'male' ? 'مرد' : 'زن'}</span>
            </div>
            <div className="flex">
              <span className="font-medium ml-2">نسبت بیمار:</span>
              <span>
                {formData.patientInfo.patientType === 'self' ? 'خودم' :
                  formData.patientInfo.patientType === 'child' ? 'فرزند' :
                    formData.patientInfo.patientType === 'parent' ? 'والدین' :
                      formData.patientInfo.patientType === 'spouse' ? 'همسر' : 'سایر'}
              </span>
            </div>
            <div className="flex">
              <span className="font-medium ml-2">سن:</span>
              <span>{formData.ageInfo.age} سال</span>
            </div>
          </div>
        </div>

        {/* Sampling Location and Address Info Section */}
        <div className="mb-8 border-b pb-4">
          <h4 className="font-bold text-blue-700 mb-4">محل نمونه گیری</h4>
          <div className="mb-4">
            <div className="flex flex-wrap mb-2">
              <span className="font-medium ml-2">نوع نمونه گیری:</span>
              <span className={`px-2 py-1 rounded-full text-xs ${formData.samplingLocation.type === 'home'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-blue-100 text-blue-800'
                }`}>
                {formData.samplingLocation.type === 'home' ? 'نمونه گیری در منزل' : 'نمونه گیری در آزمایشگاه'}
              </span>
            </div>

            {/* Only show address if home sampling is selected */}
            {formData.samplingLocation.type === 'home' && (
              <div>
                <div className="flex flex-wrap mb-2">
                  <span className="font-medium ml-2">منطقه:</span>
                  <span>{formData.addressInfo.region}</span>
                </div>
                <div className="flex flex-wrap">
                  <span>{formData.addressInfo.field && `میدان ${formData.addressInfo.field}، `}</span>
                  <span>{formData.addressInfo.street && `خیابان ${formData.addressInfo.street}، `}</span>
                  <span>{formData.addressInfo.alley && `کوچه ${formData.addressInfo.alley}، `}</span>
                  <span>{formData.addressInfo.building && `ساختمان ${formData.addressInfo.building}، `}</span>
                  <span>{formData.addressInfo.unit && `واحد ${formData.addressInfo.unit}، `}</span>
                  <span>{formData.addressInfo.number && `پلاک ${formData.addressInfo.number}`}</span>
                </div>
              </div>
            )}

            {/* Show lab info if lab sampling is selected */}
            {formData.samplingLocation.type === 'lab' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                <p className="text-sm text-blue-700">
                  آدرس و ساعات کاری آزمایشگاه پس از تایید درخواست به شما اطلاع داده خواهد شد.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Insurance Info Section */}
        <div className="mb-8">
          <h4 className="font-bold text-blue-700 mb-4">اطلاعات بیمه و آزمایش</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="flex">
              <span className="font-medium ml-2">نوع درخواست:</span>
              <span>{getInsuranceTypeDisplay()}</span>
            </div>

            {formData.insuranceInfo.type === 'paper' && (
              <>
                <div className="flex">
                  <span className="font-medium ml-2">بیمه پایه:</span>
                  <span>{getBasicInsuranceDisplay()}</span>
                </div>
                {formData.insuranceInfo.supplementaryInsurance && (
                  <div className="flex">
                    <span className="font-medium ml-2">بیمه تکمیلی:</span>
                    <span>{getSupplementaryInsuranceDisplay()}</span>
                  </div>
                )}
              </>
            )}

            {(formData.insuranceInfo.type as string) === 'electronic' && (
              <>
                <div className="flex">
                  <span className="font-medium ml-2">بیمه پایه:</span>
                  <span>{getBasicInsuranceDisplay()}</span>
                </div>
                {formData.insuranceInfo.supplementaryInsurance && (
                  <div className="flex">
                    <span className="font-medium ml-2">بیمه تکمیلی:</span>
                    <span>{getSupplementaryInsuranceDisplay()}</span>
                  </div>
                )}
                <div className="flex">
                  <span className="font-medium ml-2">کد رهگیری نسخه:</span>
                  <span>{formData.insuranceInfo.trackingCode}</span>
                </div>
              </>
            )}

            {formData.insuranceInfo.type === 'package' && (
              <div className="col-span-2">
                <span className="font-medium mb-2 block">پکیج انتخاب شده:</span>
                {(() => {
                  const selectedPackage = testPackages.find(pkg => pkg._id === formData.insuranceInfo.selectedPackage);
                  if (selectedPackage) {
                    return (
                      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-slate-800">{selectedPackage.name}</h4>
                          <div className="text-left">
                            {selectedPackage.discountPrice && selectedPackage.discountPrice < selectedPackage.price ? (
                              <div>
                                <span className="text-sm text-slate-500 line-through">
                                  {selectedPackage.price.toLocaleString('fa-IR')} تومان
                                </span>
                                <span className="text-lg font-bold text-green-600 block">
                                  {selectedPackage.discountPrice === 0 ? 'رایگان' : `${selectedPackage.discountPrice.toLocaleString('fa-IR')} تومان`}
                                </span>
                              </div>
                            ) : (
                              <span className="text-lg font-bold text-slate-700">
                                {selectedPackage.price === 0 ? 'رایگان' : `${selectedPackage.price.toLocaleString('fa-IR')} تومان`}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedPackage.description && (
                          <p className="text-sm text-slate-600">{selectedPackage.description}</p>
                        )}
                      </div>
                    );
                  } else {
                    return (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="mr-3">
                            <p className="text-sm text-yellow-800">
                              جزئیات پکیج در دسترس نیست
                            </p>
                            <p className="text-xs text-yellow-600 mt-1">
                              شناسه پکیج: {formData.insuranceInfo.selectedPackage}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            )}

            {formData.insuranceInfo.description && (
              <div className="col-span-2 flex flex-col">
                <span className="font-medium mb-1">توضیحات:</span>
                <span className="bg-slate-50 p-2 rounded-sm border border-slate-200">
                  {formData.insuranceInfo.description}
                </span>
              </div>
            )}
          </div>

          {/* Uploaded Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {renderImagePreview(formData.insuranceInfo.nationalIdImageUrl, 'تصویر کارت ملی')}
            {renderImagePreview(formData.insuranceInfo.testListImageUrl, 'لیست آزمایش‌ها')}
            {renderImagePreview(formData.insuranceInfo.prescriptionImageUrl, 'تصویر نسخه')}
            {renderImagePreview(formData.insuranceInfo.insuranceBookletImageUrl, 'تصویر دفترچه بیمه')}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 text-sm">
          <p className="font-medium text-blue-700">
            {isEditMode
              ? 'با ویرایش این درخواست، اطلاعات شما به‌روزرسانی شده و برای بررسی مجدد ارسال خواهد شد.'
              : 'با زدن دکمه ثبت درخواست، اطلاعات شما ثبت شده و کارشناسان ما در اسرع وقت با شما تماس خواهند گرفت.'
            }
          </p>
        </div>
      </div>
    );
  };

  const renderPatientInfoStep = () => {
    const patientTypes = ['خودم', 'فرزند', 'همسر', 'پدر', 'مادر', 'سایر'];
    const patientTypesEn = ['self', 'child', 'spouse', 'father', 'mother', 'other'];

    const handlePatientTypeSelect = (option: string) => {
      const index = patientTypes.findIndex(type => type === option);
      if (index !== -1) {
        const newPatientType = patientTypesEn[index];
        handleFormDataChange('patientInfo', 'patientType', newPatientType);

        // If family member is selected, populate from family data
        if (newPatientType !== 'self' && newPatientType !== 'other' && familyData) {
          let memberData: {
            firstName?: string;
            lastName?: string;
            nationalId?: string;
            mobile?: string;
            gender?: string;
          } | null = null;
          
          if (newPatientType === 'spouse') memberData = familyData.spouse;
          else if (newPatientType === 'child') memberData = familyData.children;
          else if (newPatientType === 'father') memberData = familyData.father;
          else if (newPatientType === 'mother') memberData = familyData.mother;

          if (memberData) {
            setFormData(prev => ({
              ...prev,
              patientInfo: {
                ...prev.patientInfo,
                firstName: memberData?.firstName || '',
                lastName: memberData?.lastName || '',
                nationalId: memberData?.nationalId || '',
                mobile: memberData?.mobile || '', // Populate mobile from family data
                gender: memberData?.gender || ''
              }
            }));
          }
        }
      }
    };

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">تکمیل مشخصات بیمار</h3>

        <div className="mb-4">
          <label className="block mb-1 font-medium">آزمایش برای چه کسی است؟</label>
          <SelectButton
            options={patientTypes}
            placeholder="انتخاب کنید"
            onSelect={handlePatientTypeSelect}
            value={patientTypes.find((_, index) => patientTypesEn[index] === formData.patientInfo.patientType) || ""}
          />
          {errors['patientInfo.patientType'] && (
            <p className="text-red-500 text-sm mt-1">{errors['patientInfo.patientType']}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium">نام</label>
            <input
              type="text"
              className="w-full border border-slate-300 rounded-sm p-2"
              value={formData.patientInfo.firstName}
              onChange={(e) => handleFormDataChange('patientInfo', 'firstName', e.target.value)}
              placeholder="نام بیمار را وارد کنید"
              title="نام بیمار"
              aria-label="نام بیمار"
              data-error="patientInfo.firstName"
            />
            {errors['patientInfo.firstName'] && (
              <p className="text-red-500 text-sm mt-1">{errors['patientInfo.firstName']}</p>
            )}
          </div>
          <div>
            <label className="block mb-1 font-medium">نام خانوادگی</label>
            <input
              type="text"
              className="w-full border border-slate-300 rounded-sm p-2"
              value={formData.patientInfo.lastName}
              onChange={(e) => handleFormDataChange('patientInfo', 'lastName', e.target.value)}
              placeholder="نام خانوادگی بیمار را وارد کنید"
              title="نام خانوادگی بیمار"
              aria-label="نام خانوادگی بیمار"
              data-error="patientInfo.lastName"
            />
            {errors['patientInfo.lastName'] && (
              <p className="text-red-500 text-sm mt-1">{errors['patientInfo.lastName']}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium">کد ملی</label>
            <input
              type="text"
              className={`w-full border rounded-sm p-2 ${errors['patientInfo.nationalId']
                  ? 'border-red-500 bg-red-50'
                  : formData.patientInfo.nationalId && validateIranianNationalCode(formData.patientInfo.nationalId).isValid
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-300'
                }`}
              value={formData.patientInfo.nationalId}
              onChange={(e) => handleFormDataChange('patientInfo', 'nationalId', e.target.value)}
              placeholder="کد ملی را وارد کنید"
              title="کد ملی بیمار"
              aria-label="کد ملی بیمار"
              data-error="patientInfo.nationalId"
              maxLength={10}
              inputMode="numeric"
            />
            {errors['patientInfo.nationalId'] && (
              <p className="text-red-500 text-sm mt-1">{errors['patientInfo.nationalId']}</p>
            )}
          </div>
          <div>
            <label className="block mb-1 font-medium">موبایل</label>
            <input
              type="tel"
              className={`w-full border rounded-sm p-2 ${errors['patientInfo.mobile']
                  ? 'border-red-500 bg-red-50'
                  : formData.patientInfo.mobile && validateMobileNumber(formData.patientInfo.mobile).isValid
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-300'
                }`}
              value={formData.patientInfo.mobile}
              onChange={(e) => handleFormDataChange('patientInfo', 'mobile', e.target.value)}
              placeholder="شماره موبایل را وارد کنید (09xxxxxxxxx)"
              title="شماره موبایل بیمار"
              aria-label="شماره موبایل بیمار"
              data-error="patientInfo.mobile"
              maxLength={11}
              inputMode="numeric"
            />
            {errors['patientInfo.mobile'] && (
              <p className="text-red-500 text-sm mt-1">{errors['patientInfo.mobile']}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block mb-1 font-medium">جنسیت</label>
          <div className="flex gap-6" data-error="patientInfo.gender">
            <label
              className={`flex items-center space-x-2 space-x-reverse group ${formData.patientInfo.patientType === 'mother'
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
                }`}
            >
              <div className="relative">
                <input
                  type="radio"
                  name="gender"
                  value="male"
                  title="مرد"
                  aria-label="مرد"
                  checked={formData.patientInfo.gender === 'male'}
                  onChange={() => handleFormDataChange('patientInfo', 'gender', 'male')}
                  className="sr-only" /* Hidden but accessible */
                  disabled={formData.patientInfo.patientType === 'mother'}
                />
                <div
                  className={`
                  w-5 h-5 rounded-full border-2 transition-colors duration-200
                  ${formData.patientInfo.gender === 'male'
                      ? 'border-blue-500 bg-white'
                      : 'border-slate-300 group-hover:border-slate-400'}
                `}
                ></div>
                <div
                  className={`
                  absolute inset-0 m-auto w-2.5 h-2.5 rounded-full transition-colors duration-200
                  pointer-events-none transform scale-0 
                  ${formData.patientInfo.gender === 'male'
                      ? 'bg-blue-500 scale-100'
                      : 'bg-transparent'}
                `}
                ></div>
              </div>
              <span className="text-slate-700 mr-2">مرد</span>
            </label>

            <label
              className={`flex items-center space-x-2 space-x-reverse group ${formData.patientInfo.patientType === 'father'
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
                }`}
            >
              <div className="relative">
                <input
                  type="radio"
                  name="gender"
                  value="female"
                  title="زن"
                  aria-label="زن"
                  checked={formData.patientInfo.gender === 'female'}
                  onChange={() => handleFormDataChange('patientInfo', 'gender', 'female')}
                  className="sr-only" /* Hidden but accessible */
                  disabled={formData.patientInfo.patientType === 'father'}
                />
                <div
                  className={`
                  w-5 h-5 rounded-full border-2 transition-colors duration-200
                  ${formData.patientInfo.gender === 'female'
                      ? 'border-blue-500 bg-white'
                      : 'border-slate-300 group-hover:border-slate-400'}
                `}
                ></div>
                <div
                  className={`
                  absolute inset-0 m-auto w-2.5 h-2.5 rounded-full transition-colors duration-200
                  pointer-events-none transform scale-0 
                  ${formData.patientInfo.gender === 'female'
                      ? 'bg-blue-500 scale-100'
                      : 'bg-transparent'}
                `}
                ></div>
              </div>
              <span className="text-slate-700 mr-2">زن</span>
            </label>
          </div>
          {errors['patientInfo.gender'] && (
            <p className="text-red-500 text-sm mt-1">{errors['patientInfo.gender']}</p>
          )}
        </div>
      </div>
    );
  };

  const renderAddressStep = () => {
    // Prepare region options for the SelectButton
    const regionOptions = regions.map(region => {
      const streetsText = region.streets && region.streets.length > 0
        ? ` (${region.streets.join('، ')})`
        : '';
      return region.name + streetsText;
    });
    regionOptions.push('سایر مناطق');
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold mb-4">تعیین محل نمونه گیری</h3>

        {/* Sampling Location Selection */}
        <div className="mb-6">
          <label className="block mb-3 font-medium">محل نمونه گیری</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${formData.samplingLocation.type === 'home'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
                }`}
              onClick={() => handleFormDataChange('samplingLocation', 'type', 'home')}
            >
              <div className="flex items-center mb-2">
                <div className={`w-4 h-4 rounded-full border-2 mr-3 ${formData.samplingLocation.type === 'home'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                  }`}>
                  {formData.samplingLocation.type === 'home' && (
                    <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                  )}
                </div>
                <h4 className="font-semibold text-gray-800">نمونه گیری در منزل</h4>
              </div>
              <p className="text-sm text-gray-600 mr-7">
                نمونه گیری در آدرس شما انجام می‌شود
              </p>
            </div>

            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${formData.samplingLocation.type === 'lab'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
                }`}
              onClick={() => handleFormDataChange('samplingLocation', 'type', 'lab')}
            >
              <div className="flex items-center mb-2">
                <div className={`w-4 h-4 rounded-full border-2 mr-3 ${formData.samplingLocation.type === 'lab'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                  }`}>
                  {formData.samplingLocation.type === 'lab' && (
                    <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                  )}
                </div>
                <h4 className="font-semibold text-gray-800">نمونه گیری در آزمایشگاه</h4>
              </div>
              <p className="text-sm text-gray-600 mr-7">
                مراجعه به آزمایشگاه برای نمونه گیری
              </p>
            </div>
          </div>
          {errors['samplingLocation.type'] && (
            <p className="text-red-500 text-sm mt-1">{errors['samplingLocation.type']}</p>
          )}
        </div>

        {/* Address Fields - Only show if home sampling is selected */}
        {formData.samplingLocation.type === 'home' && (
          <div className="space-y-4 border-t pt-6">
            <h4 className="text-md font-semibold text-gray-700 mb-4">آدرس محل نمونه گیری</h4>

            <div className="mb-4">
              <label className="block mb-1 font-medium">منطقه</label>
              {regionsLoading ? (
                <div className="w-full border border-slate-300 rounded-sm p-2 bg-gray-50 text-gray-500">
                  در حال بارگیری مناطق...
                </div>
              ) : regionsError ? (
                <div className="w-full border border-red-300 rounded-sm p-2 bg-red-50 text-red-600 text-sm">
                  {regionsError} - از مناطق پیش‌فرض استفاده می‌شود
                </div>
              ) : null}
              <SelectButton
                options={regionOptions}
                placeholder={regionsLoading ? "در حال بارگیری..." : "انتخاب منطقه"}
                onSelect={(option) => {
                  // Extract just the region name (before the parentheses)
                  const regionName = option.includes(' (') ? option.split(' (')[0] : option;
                  handleFormDataChange('addressInfo', 'region', regionName);
                  // Show MX4 message if "سایر مناطق" is selected
                  if (regionName === 'سایر مناطق') {
                    const mx4Message = messageCenter?.messageCenter?.MX4 || 'متأسفانه منطقه انتخابی شما تحت پوشش نمونه‌گیری در منزل نمی‌باشد.';
                    toast.error(mx4Message);
                    setShowCancelButton(true);

                    // Auto-save draft when "سایر مناطق" is selected
                    setTimeout(() => {
                      saveDraftInternal(true);
                    }, 1000); // Small delay to show the error message first
                  } else {
                    setShowCancelButton(false);
                  }
                }}
                value={regionOptions.find(option =>
                  option.includes(' (') ? option.split(' (')[0] === formData.addressInfo.region : option === formData.addressInfo.region
                ) || formData.addressInfo.region}
                data-error="addressInfo.region"
              />
              {errors['addressInfo.region'] && (
                <p className="text-red-500 text-sm mt-1">{errors['addressInfo.region']}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block mb-1 font-medium">میدان</label>
              <input
                type="text"
                className="w-full border border-slate-300 rounded-sm p-2"
                value={formData.addressInfo.field || ''}
                onChange={(e) => handleFormDataChange('addressInfo', 'field', e.target.value)}
                placeholder="میدان را وارد کنید"
                title="میدان"
                aria-label="میدان"
                data-error="addressInfo.field"
              />
              {errors['addressInfo.field'] && (
                <p className="text-red-500 text-sm mt-1">{errors['addressInfo.field']}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block mb-1 font-medium">خیابان</label>
              <input
                type="text"
                className="w-full border border-slate-300 rounded-sm p-2"
                value={formData.addressInfo.street || ''}
                onChange={(e) => handleFormDataChange('addressInfo', 'street', e.target.value)}
                placeholder="خیابان را وارد کنید"
                title="خیابان"
                aria-label="خیابان"
                data-error="addressInfo.street"
              />
              {errors['addressInfo.street'] && (
                <p className="text-red-500 text-sm mt-1">{errors['addressInfo.street']}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 font-medium">کوچه</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-sm p-2"
                  value={formData.addressInfo.alley || ''}
                  onChange={(e) => handleFormDataChange('addressInfo', 'alley', e.target.value)}
                  placeholder="کوچه را وارد کنید"
                  title="کوچه"
                  aria-label="کوچه"
                  data-error="addressInfo.alley"
                />
                {errors['addressInfo.alley'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['addressInfo.alley']}</p>
                )}
              </div>
              <div>
                <label className="block mb-1 font-medium">ساختمان</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-sm p-2"
                  value={formData.addressInfo.building || ''}
                  onChange={(e) => handleFormDataChange('addressInfo', 'building', e.target.value)}
                  placeholder="نام ساختمان را وارد کنید (اختیاری)"
                  title="ساختمان"
                  aria-label="ساختمان"
                  data-error="addressInfo.building"
                />
                {errors['addressInfo.building'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['addressInfo.building']}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 font-medium">واحد</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-sm p-2"
                  value={formData.addressInfo.unit || ''}
                  onChange={(e) => handleFormDataChange('addressInfo', 'unit', e.target.value)}
                  placeholder="شماره واحد را وارد کنید"
                  title="واحد"
                  aria-label="واحد"
                  data-error="addressInfo.unit"
                />
                {errors['addressInfo.unit'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['addressInfo.unit']}</p>
                )}
              </div>
              <div>
                <label className="block mb-1 font-medium">پلاک</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-sm p-2"
                  value={formData.addressInfo.number}
                  onChange={(e) => handleFormDataChange('addressInfo', 'number', e.target.value)}
                  placeholder="شماره پلاک را وارد کنید"
                  title="پلاک"
                  aria-label="پلاک"
                  data-error="addressInfo.number"
                />
              </div>
            </div>
          </div>
        )}

        {/* Lab Information - Show if lab sampling is selected */}
        {formData.samplingLocation.type === 'lab' && (
          <div className="space-y-4 border-t pt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-md font-semibold text-blue-800 mb-2">نمونه گیری در آزمایشگاه</h4>
              <p className="text-sm text-blue-700">
                شما انتخاب کرده‌اید که برای نمونه گیری به آزمایشگاه مراجعه کنید.
                آدرس و ساعات کاری آزمایشگاه پس از تایید درخواست به شما اطلاع داده خواهد شد.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAgeStep = () => {
    // Handle age input change - only update form data and clear errors
    const handleAgeChange = (value: string) => {
      handleFormDataChange('ageInfo', 'age', value);

      // Clear any existing errors when user starts typing
      if (errors['ageInfo.age']) {
        setErrors({
          ...errors,
          'ageInfo.age': '',
        });
      }
    };

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">سن بیمار</h3>

        <div>
          <label className="block mb-1 font-medium">سن (به سال)</label>
          <input
            type="number"
            min="5"
            className="w-full border border-slate-300 rounded-sm p-2"
            value={formData.ageInfo.age}
            onChange={(e) => handleAgeChange(e.target.value)}
            placeholder="سن بیمار را وارد کنید (حداقل 5 سال)"
            title="سن بیمار"
            aria-label="سن بیمار"
            data-error="ageInfo.age"
          />
          {errors['ageInfo.age'] && (
            <p className="text-red-500 text-sm mt-1">{errors['ageInfo.age']}</p>
          )}
        </div>
      </div>
    );
  };

  // File upload component that uses our ModernFileUploader
  const FileUpload = ({ id, label, onChange, required = false, error }: {
    id: string;
    label: string;
    onChange: (file: File, fileUrl: string) => void;
    required?: boolean;
    error?: string;
  }) => (
    <ModernFileUploader
      id={id}
      label={label}
      onChange={onChange}
      required={required}
      error={error}
      accept="image/*, application/pdf"
    />
  );

  const renderInsuranceStep = () => {
    const insuranceTypes = [
      { id: 'free', name: 'بدون نسخه (آزاد)' },
      { id: 'paper', name: 'با نسخه کاغذی پزشک' },
      { id: 'electronic', name: 'نسخه الکترونیک' },
      { id: 'package', name: 'پکیجهای آماده' }
    ];

    // Convert dynamic insurances to options format for dropdown
    const insuranceOptions = [
      { id: 'none', name: 'هیچکدام', requiresExtraCode: false, requiresInsuranceBooklet: false },
      ...insurances.map(insurance => ({
      id: insurance._id,
      name: insurance.name,
      requiresExtraCode: insurance.requiresExtraCode,
      requiresInsuranceBooklet: insurance.requiresInsuranceBooklet
      }))
    ];

    // Convert test packages to options format for dropdown
    const packagesOptions = testPackages.map(pkg => ({
      id: pkg._id,
      name: pkg.name,
      price: pkg.price,
      discountPrice: pkg.discountPrice,
      description: pkg.description
    }));

    // Handle file uploads with URL
    const handleFileUpload = (fieldName: string, file: File, fileUrl: string) => {
      // Update file object
      handleFormDataChange('insuranceInfo', fieldName, file);
      // Update file URL
      handleFormDataChange('insuranceInfo', `${fieldName}Url`, fileUrl);

      // Clear any existing errors for this field
      if (errors[`insuranceInfo.${fieldName}`]) {
        setErrors({
          ...errors,
          [`insuranceInfo.${fieldName}`]: '',
        });
      }
    };

    // Modern file upload component using our new ModernFileUploader
    const FileUpload = ({ id, label, onChange, required = false }: { id: string, label: string, onChange: (file: File, fileUrl: string) => void, required?: boolean }) => {
      // Get the URL from formData if it exists
      const fileUrlKey = `${id}Url` as keyof typeof formData.insuranceInfo;
      const imageUrl = formData.insuranceInfo[fileUrlKey] as string | undefined;

      return (
        <div className="mb-4" data-error={`insuranceInfo.${id}`}>
          <ModernFileUploader
            id={id}
            label={label}
            onChange={onChange}
            required={required}
            error={errors[`insuranceInfo.${id}`]}
            accept="image/*"
          />

          {/* Display the uploaded image if available */}
          {imageUrl && imageUrl.length > 0 && (
            <div className="mt-2 border rounded-sm p-2">
              <h5 className="text-sm font-medium mb-2">تصویر آپلود شده:</h5>
              <div className="flex justify-center">
                <img
                  src={imageUrl.startsWith('http') ? imageUrl : `${API_CONFIG.uploadURL}${imageUrl}`}
                  alt={label}
                  className="max-w-full h-auto max-h-48 rounded-sm"
                />
              </div>
            </div>
          )}
        </div>
      );
    };
    const renderRequirements = () => {
      switch (formData.insuranceInfo.type) {
        case 'free': // بدون نسخه (آزاد)
          return (
            <div className="mt-6 border border-slate-200 rounded-sm p-4 bg-slate-50">
              <h4 className="text-md font-semibold mb-4">مدارک مورد نیاز</h4>

              <FileUpload
                id="nationalIdImage"
                label="آپلود تصویر کارت ملی"
                onChange={(file, fileUrl) => handleFileUpload('nationalIdImage', file, fileUrl)}
                required={true}
              />

              <FileUpload
                id="testListImage"
                label="آپلود تصویر نام آزمایش های مورد نظر"
                onChange={(file, fileUrl) => handleFileUpload('testListImage', file, fileUrl)}
              />

              <div className="mb-4">
                <label className="block mb-1 font-medium">
                  ثبت توضیحات جهت تعیین نوع تست
                  <span className="text-red-500 mr-1">*</span>
                </label>
                <textarea
                  className="w-full border border-slate-300 rounded-sm p-2 h-24"
                  value={formData.insuranceInfo.description || ''}
                  onChange={(e) => handleFormDataChange('insuranceInfo', 'description', e.target.value)}
                  placeholder="لطفا توضیحات لازم برای تعیین نوع تست را وارد کنید. در صورت مصرف هر کدام از داروهای آسپرین، وارفارین، تیروئید و آهن در این کادر ذکر فرمایید"
                />
                {errors['insuranceInfo.description'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['insuranceInfo.description']}</p>
                )}
              </div>
            </div>
          );

        case 'paper': // با نسخه کاغذی پزشک
          return (
            <div className="mt-6 border border-slate-200 rounded-sm p-4 bg-slate-50">
              <h4 className="text-md font-semibold mb-4">مدارک مورد نیاز</h4>

              <div className="mb-4">
                <label id="basicInsurancePaper" className="block mb-1 font-medium">وضعیت بیمه پایه</label>
                {insurancesLoading ? (
                  <div className="w-full border border-slate-300 rounded-sm p-2 bg-gray-50 text-gray-500">
                    در حال بارگیری بیمه ها...
                  </div>
                ) : insurancesError ? (
                  <div className="w-full border border-red-300 rounded-sm p-2 bg-red-50 text-red-600 text-sm">
                    {insurancesError} - از بیمه های پیش‌فرض استفاده می‌شود
                  </div>
                ) : null}
                <select
                  className="w-full border border-slate-300 rounded-sm p-2"
                  value={formData.insuranceInfo.basicInsurance || ''}
                  onChange={(e) => handleFormDataChange('insuranceInfo', 'basicInsurance', e.target.value)}
                  aria-labelledby="basicInsurancePaper"
                  title="وضعیت بیمه پایه"
                  data-error="insuranceInfo.basicInsurance"
                  disabled={insurancesLoading}
                >
                  <option value="">انتخاب کنید</option>
                  {insuranceOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
                {errors['insuranceInfo.basicInsurance'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['insuranceInfo.basicInsurance']}</p>
                )}
              </div>

              {/* Insurance Code Field - Show only if selected insurance requires extra code */}
              {formData.insuranceInfo.basicInsurance && formData.insuranceInfo.basicInsurance !== 'none' && (() => {
                const selectedInsurance = insuranceOptions.find(option => option.id === formData.insuranceInfo.basicInsurance);
                return null;
              })()}

              <div className="mb-4">
                <label id="supplementaryInsurancePaper" className="block mb-1 font-medium">بیمه تکمیلی <span className="text-red-500 mr-1">*</span></label>
                {supplementaryInsurancesLoading ? (
                  <div className="w-full border border-slate-300 rounded-sm p-2 bg-gray-50 text-gray-500">
                    در حال بارگیری بیمه های تکمیلی...
                  </div>
                ) : supplementaryInsurancesError ? (
                  <div className="w-full border border-red-300 rounded-sm p-2 bg-red-50 text-red-600 text-sm">
                    {supplementaryInsurancesError} - از بیمه های پیش‌فرض استفاده می‌شود
                  </div>
                ) : null}
                <select
                  className="w-full border border-slate-300 rounded-sm p-2"
                  value={formData.insuranceInfo.supplementaryInsurance || ''}
                  onChange={(e) => handleFormDataChange('insuranceInfo', 'supplementaryInsurance', e.target.value)}
                  aria-labelledby="supplementaryInsurancePaper"
                  title="بیمه تکمیلی"
                  disabled={supplementaryInsurancesLoading}
                  data-error="insuranceInfo.supplementaryInsurance"
                >
                  <option value="">انتخاب کنید</option>
                  <option value="none">هیچکدام</option>
                  {supplementaryInsurances.map(supplementaryInsurance => (
                    <option key={supplementaryInsurance._id} value={supplementaryInsurance._id}>
                      {supplementaryInsurance.name}
                    </option>
                  ))}
                </select>
                {errors['insuranceInfo.supplementaryInsurance'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['insuranceInfo.supplementaryInsurance']}</p>
                )}
              </div>

              <FileUpload
                id="nationalIdImage"
                label="آپلود تصویر کارت ملی"
                onChange={(file, fileUrl) => handleFileUpload('nationalIdImage', file, fileUrl)}
                required={true}
              />

              <FileUpload
                id="prescriptionImage"
                label="آپلود تصویر نسخه پزشک"
                onChange={(file, fileUrl) => handleFileUpload('prescriptionImage', file, fileUrl)}
                required={true}
              />

              {/* Insurance Booklet Upload - Show only if selected insurance requires it */}
              {formData.insuranceInfo.basicInsurance && formData.insuranceInfo.basicInsurance !== 'none' && (() => {
                const selectedInsurance = insuranceOptions.find(option => option.id === formData.insuranceInfo.basicInsurance);
                return selectedInsurance?.requiresInsuranceBooklet ? (
                  <FileUpload
                    id="insuranceBookletImage"
                    label={`آپلود تصویر دفترچه (صفحه اول) ${selectedInsurance.name}`}
                    onChange={(file, fileUrl) => handleFileUpload('insuranceBookletImage', file, fileUrl)}
                    required={true}
                  />
                ) : null;
              })()}

              <div className="mb-4">
                <label className="block mb-1 font-medium">توضیحات (در صورت نیاز)</label>
                <textarea
                  className="w-full border border-slate-300 rounded-sm p-2 h-24"
                  value={formData.insuranceInfo.description || ''}
                  onChange={(e) => handleFormDataChange('insuranceInfo', 'description', e.target.value)}
                  placeholder="توضیحات در صورت نیاز. در صورت مصرف هر کدام از داروهای آسپرین، وارفارین، تیروئید و آهن در این کادر ذکر فرمایید"
                />
              </div>
            </div>
          );

        case 'electronic': // نسخه الکترونیک
          return (
            <div className="mt-6 border border-slate-200 rounded-sm p-4 bg-slate-50">
              <h4 className="text-md font-semibold mb-4">مدارک مورد نیاز</h4>

              <div className="mb-4">
                <label id="basicInsElecLabel" className="block mb-1 font-medium">
                  وضعیت بیمه پایه
                  <span className="text-red-500 mr-1">*</span>
                </label>
                {insurancesLoading ? (
                  <div className="w-full border border-slate-300 rounded-sm p-2 bg-gray-50 text-gray-500">
                    در حال بارگیری بیمه ها...
                  </div>
                ) : insurancesError ? (
                  <div className="w-full border border-red-300 rounded-sm p-2 bg-red-50 text-red-600 text-sm">
                    {insurancesError} - از بیمه های پیش‌فرض استفاده می‌شود
                  </div>
                ) : null}
                <select
                  className="w-full border border-slate-300 rounded-sm p-2"
                  value={formData.insuranceInfo.basicInsurance || ''}
                  onChange={(e) => handleFormDataChange('insuranceInfo', 'basicInsurance', e.target.value)}
                  aria-labelledby="basicInsElecLabel"
                  title="وضعیت بیمه پایه"
                  disabled={insurancesLoading}
                >
                  <option value="">انتخاب کنید</option>
                  {insuranceOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
                {errors['insuranceInfo.basicInsurance'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['insuranceInfo.basicInsurance']}</p>
                )}
              </div>

              {/* Insurance Code Field - Not shown for electronic prescriptions */}

              {/* Tracking Code Field - Show only if selected insurance requires tracking code for electronic prescriptions */}
              {formData.insuranceInfo.basicInsurance && formData.insuranceInfo.basicInsurance !== 'none' && (() => {
                const selectedInsurance = insurances.find(ins => ins._id === formData.insuranceInfo.basicInsurance);
                return selectedInsurance?.requiresExtraCode ? (
                  <div className="mb-4">
                    <label className="block mb-1 font-medium">
                      کد رهگیری نسخه الکترونیک
                      <span className="text-red-500 mr-1">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border border-slate-300 rounded-sm p-2"
                      value={formData.insuranceInfo.trackingCode || ''}
                      onChange={(e) => handleFormDataChange('insuranceInfo', 'trackingCode', e.target.value)}
                      placeholder="کد رهگیری را وارد کنید"
                    />
                    {errors['insuranceInfo.trackingCode'] && (
                      <p className="text-red-500 text-sm mt-1">{errors['insuranceInfo.trackingCode']}</p>
                    )}
                  </div>
                ) : null;
              })()}

              <div className="mb-4">
                <label id="supplementaryInsuranceElectronic" className="block mb-1 font-medium">بیمه تکمیلی <span className="text-red-500 mr-1">*</span></label>
                {supplementaryInsurancesLoading ? (
                  <div className="w-full border border-slate-300 rounded-sm p-2 bg-gray-50 text-gray-500">
                    در حال بارگیری بیمه های تکمیلی...
                  </div>
                ) : supplementaryInsurancesError ? (
                  <div className="w-full border border-red-300 rounded-sm p-2 bg-red-50 text-red-600 text-sm">
                    {supplementaryInsurancesError} - از بیمه های پیش‌فرض استفاده می‌شود
                  </div>
                ) : null}
                <select
                  className="w-full border border-slate-300 rounded-sm p-2"
                  value={formData.insuranceInfo.supplementaryInsurance || ''}
                  onChange={(e) => handleFormDataChange('insuranceInfo', 'supplementaryInsurance', e.target.value)}
                  aria-labelledby="supplementaryInsuranceElectronic"
                  title="بیمه تکمیلی"
                  disabled={supplementaryInsurancesLoading}
                  data-error="insuranceInfo.supplementaryInsurance"
                >
                  <option value="">انتخاب کنید</option>
                  <option value="none">هیچکدام</option>
                  {supplementaryInsurances.map(supplementaryInsurance => (
                    <option key={supplementaryInsurance._id} value={supplementaryInsurance._id}>
                      {supplementaryInsurance.name}
                    </option>
                  ))}
                </select>
                {errors['insuranceInfo.supplementaryInsurance'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['insuranceInfo.supplementaryInsurance']}</p>
                )}
              </div>

              <FileUpload
                id="nationalIdImage"
                label="آپلود تصویر کارت ملی"
                onChange={(file, fileUrl) => handleFileUpload('nationalIdImage', file, fileUrl)}
                required={true}
              />

              {/* Insurance Booklet Upload for Electronic - Show only if selected insurance requires it */}
              {formData.insuranceInfo.basicInsurance && formData.insuranceInfo.basicInsurance !== 'none' && (() => {
                const selectedInsurance = insuranceOptions.find(option => option.id === formData.insuranceInfo.basicInsurance);
                return selectedInsurance?.requiresInsuranceBooklet ? (
                  <FileUpload
                    id="insuranceBookletImage"
                    label={`آپلود تصویر دفترچه (صفحه اول) ${selectedInsurance.name}`}
                    onChange={(file, fileUrl) => handleFileUpload('insuranceBookletImage', file, fileUrl)}
                    required={true}
                  />
                ) : null;
              })()}

              <div className="mb-4">
                <label className="block mb-1 font-medium">توضیحات (در صورت نیاز)</label>
                <textarea
                  className="w-full border border-slate-300 rounded-sm p-2 h-24"
                  value={formData.insuranceInfo.description || ''}
                  onChange={(e) => handleFormDataChange('insuranceInfo', 'description', e.target.value)}
                  placeholder="توضیحات در صورت نیاز. در صورت مصرف هر کدام از داروهای آسپرین، وارفارین، تیروئید و آهن در این کادر ذکر فرمایید"
                />
              </div>
            </div>
          );

        case 'package': // پکیجهای آماده
          return (
            <div className="mt-6 border border-slate-200 rounded-sm p-4 bg-slate-50">
              <h4 className="text-md font-semibold mb-4">انتخاب پکیج</h4>

              <div className="mb-4">
                <label className="block mb-1 font-medium">
                  پکیج مورد نظر
                  <span className="text-red-500 mr-1">*</span>
                </label>
                {packagesLoading ? (
                  <div className="w-full border border-slate-300 rounded-sm p-2 bg-gray-50 text-gray-500">
                    در حال بارگیری پکیج ها...
                  </div>
                ) : packagesError ? (
                  <div className="w-full border border-red-300 rounded-sm p-2 bg-red-50 text-red-600">
                    {packagesError}
                  </div>
                ) : (
                  <select
                    className="w-full border border-slate-300 rounded-sm p-2"
                    value={formData.insuranceInfo.selectedPackage || ''}
                    onChange={(e) => handleFormDataChange('insuranceInfo', 'selectedPackage', e.target.value)}
                    title="پکیج مورد نظر"
                    aria-label="پکیج مورد نظر"
                    data-error="insuranceInfo.selectedPackage"
                  >
                    <option value="">انتخاب کنید</option>
                    {packagesOptions.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                        {option.discountPrice > 0 && ` - ${new Intl.NumberFormat('fa-IR').format(option.discountPrice)} تومان`}
                      </option>
                    ))}
                  </select>
                )}
                {errors['insuranceInfo.selectedPackage'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['insuranceInfo.selectedPackage']}</p>
                )}

                {/* Show selected package details */}
                {formData.insuranceInfo.selectedPackage && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                    {(() => {
                      const selectedPackage = packagesOptions.find(pkg => pkg.id === formData.insuranceInfo.selectedPackage);
                      if (selectedPackage) {
                        return (
                          <div>
                            <h5 className="font-medium text-blue-800">{selectedPackage.name}</h5>
                            {selectedPackage.description && (
                              <p className="text-sm text-blue-600 mt-1">{selectedPackage.description}</p>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                              {selectedPackage.price > selectedPackage.discountPrice && (
                                <span className="text-sm text-gray-500 line-through">
                                  {new Intl.NumberFormat('fa-IR').format(selectedPackage.price)} تومان
                                </span>
                              )}
                              <span className="text-sm font-medium text-blue-800">
                                {selectedPackage.discountPrice === 0
                                  ? 'رایگان'
                                  : `${new Intl.NumberFormat('fa-IR').format(selectedPackage.discountPrice)} تومان`
                                }
                              </span>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </div>

              <FileUpload
                id="nationalIdImage"
                label="آپلود تصویر کارت ملی"
                onChange={(file, fileUrl) => handleFileUpload('nationalIdImage', file, fileUrl)}
                required={true}
              />

              <div className="mb-4">
                <label className="block mb-1 font-medium">توضیحات (در صورت نیاز)</label>
                <textarea
                  className="w-full border border-slate-300 rounded-sm p-2 h-24"
                  value={formData.insuranceInfo.description || ''}
                  onChange={(e) => handleFormDataChange('insuranceInfo', 'description', e.target.value)}
                  placeholder="توضیحات در صورت نیاز. در صورت مصرف هر کدام از داروهای آسپرین، وارفارین، تیروئید و آهن در این کادر ذکر فرمایید"
                />
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">اطلاعات بیمه</h3>

        <div className="mb-6" data-error="insuranceInfo.type">
          <label className="block mb-2 font-medium">نوع درخواست آزمایش</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {insuranceTypes.map(type => (
              <div
                key={type.id}
                className={`border rounded-sm p-3 text-center cursor-pointer hover:bg-slate-50 transition duration-150 ${formData.insuranceInfo.type === type.id ? 'border-blue-500 bg-blue-50' : 'border-slate-300'}`}
                onClick={() => handleFormDataChange('insuranceInfo', 'type', type.id)}
              >
                <div className="text-sm font-medium">{type.name}</div>
              </div>
            ))}
          </div>
          {errors['insuranceInfo.type'] && (
            <p className="text-red-500 text-sm mt-1">{errors['insuranceInfo.type']}</p>
          )}
        </div>

        {formData.insuranceInfo.type && renderRequirements()}
      </div>
    );
  };

  // Show loading state when loading existing request in edit mode
  if (loadingRequest) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          <span className="mr-3">در حال بارگیری اطلاعات درخواست...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4">
        <div className="card bg-white rounded-sm p-6 border border-slate-300">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-800">
              {isEditMode && originalRequestStatus !== 'incomplete' ? 'ویرایش درخواست آزمایش' : 'درخواست آزمایش جدید'}
            </h2>
            {isEditMode && (
              <span className="text-sm text-gray-500">شناسه: {id?.slice(-8)}</span>
            )}
          </div>

          {renderStepIndicator()}

          <div className="mb-8">
            {renderStep()}
          </div>

          <div className="flex justify-between mt-6">
            {currentStep > 1 && (
              <button
                onClick={prevStep}
                className="px-4 py-2 border border-slate-300 rounded-sm text-slate-700 hover:bg-slate-100"
              >
                قبلی
              </button>
            )}
            <div className="flex-grow"></div>

            {/* Save Draft button - show after step 1 and before final step */}
            {currentStep >= 1 && currentStep < 5 && (
              <button
                hidden
                onClick={saveDraft}
                className="px-4 py-2 border border-orange-300 bg-orange-50 rounded-sm text-orange-700 hover:bg-orange-100 ml-2"
              >
                ذخیره پیش‌نویس
              </button>
            )}

            {currentStep < 5 ? (
              <div className="flex items-center gap-2">
                {showCancelButton && (
                  <button
                    onClick={() => navigate('/my-tests')}
                    className="px-4 py-2 border border-red-300 bg-red-50 rounded-sm text-red-700 hover:bg-red-100"
                  >
                    انصراف
                  </button>
                )}
              <button
                onClick={nextStep}
                disabled={isNextButtonDisabled}
                className={`px-4 py-2 rounded-sm text-white ${
                  isNextButtonDisabled 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isNextButtonDisabled 
                  ? `لطفا صبر کنید... (${countdownTimer})` 
                  : (currentStep === 4 ? 'بررسی نهایی' : 'بعدی')
                }
              </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">

                {!isEditMode && (

                  <button
                    onClick={() => setCurrentStep(1)}
                    className="px-4 py-2 bg-blue-600 rounded-sm text-white hover:bg-blue-700"
                  >
                    ویرایش درخواست
                  </button>
                )}

                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-green-600 rounded-sm text-white hover:bg-green-700"
                >
                  {isEditMode && originalRequestStatus !== 'incomplete' ? 'ویرایش درخواست' : 'ثبت درخواست'}

                </button>

              </div>

            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NewTestRequest;
